'use client';

import { useFormatter, useTranslations } from 'next-intl';
import type { ComponentProps, CSSProperties } from 'react';
import type { LeaderboardEntry } from '@/lib/api/rewards-leaderboard/models';
import { GlowName } from '@/lib/components/RewardsHub/GlowName';
import { playerProfileHref } from '@/lib/components/RewardsHub/routes';
import { WHOLE_USD_FORMAT } from '@/lib/components/SearchPopoverV2/core/constants';
import { Skeleton } from '@/lib/ui-kit/skeleton';
import { cn } from '@/lib/ui-kit/utils';
import { UserAvatar } from '@/lib/ui-kit-v2/user-avatar';
import { PlaceCoin } from './PlaceCoin';
import { SidebarLink } from './SidebarLink';

interface EntryProps {
  entry: LeaderboardEntry;
}

interface RowProps extends EntryProps {
  /** The row replaces a skeleton, so it fades up in a stagger. */
  reveal?: boolean;
  /** 0-based step of the row in that stagger. */
  order?: number;
}

interface RevealStyle {
  className?: string;
  style?: CSSProperties;
}

const REVEAL_STEP_MS = 60;
const REVEAL_CLASS =
  'animate-in fade-in slide-in-from-bottom-1 duration-300 motion-reduce:animate-none';

function revealStyle(reveal: boolean, order: number): RevealStyle {
  if (!reveal) return {};
  return {
    className: REVEAL_CLASS,
    // `backwards` holds the part hidden through its delay
    style: { animationDelay: `${order * REVEAL_STEP_MS}ms`, animationFillMode: 'backwards' },
  };
}

interface FiguresProps extends EntryProps, Pick<ComponentProps<'span'>, 'className' | 'style'> {}

/** Volume over tasks, right-aligned (Figma 15001:34576). */
function Figures({ entry, className, style }: FiguresProps) {
  const t = useTranslations('home.leaderboard');
  const format = useFormatter();
  return (
    <span
      className={cn('flex shrink-0 flex-col items-end gap-1 text-right tabular-nums', className)}
      style={style}
    >
      <span className="text-[12px] leading-4 tracking-[-0.12px] text-v2-text-primary">
        {format.number(entry.volume, WHOLE_USD_FORMAT)}
      </span>
      <span className="text-[10px] leading-[normal] text-v2-text-secondary">
        {t('tasks', { count: entry.tasks })}
      </span>
    </span>
  );
}

function YouTag() {
  const t = useTranslations('rewardsHub.leaderboard');
  return (
    <span className="shrink-0 rounded-[8px] px-2 py-[2px] text-[12px] leading-4 tracking-[-0.12px] text-blue-500">
      {t('you')}
    </span>
  );
}

/** The underline sits on this span, not on `GlowName`: a metal name's own colour is transparent. */
function PlayerName({ entry }: EntryProps) {
  const t = useTranslations('rewardsHub.leaderboard');
  return (
    <span className="truncate text-[13px] font-[500] leading-4 tracking-[-0.13px] text-v2-text-primary group-hover/player:underline">
      <GlowName glow={entry.look?.glow ?? null}>{entry.username ?? t('noNickname')}</GlowName>
    </span>
  );
}

const ROW_LINK_CLASS =
  'group/player flex min-w-0 flex-1 items-center gap-2 rounded-[8px] outline-none focus-visible:ring-2 focus-visible:ring-v2-bg-blue-50';

/**
 * One of the top three: place disc, portrait and name (both open the profile),
 * then figures. On reveal the disc stays put; the rest fades up.
 */
export function LeaderRow({ entry, reveal = false, order = 0 }: RowProps) {
  const { className: revealClass, style } = revealStyle(reveal, order);
  return (
    <div className="flex h-10 items-center gap-2">
      <PlaceCoin place={entry.position} />
      <SidebarLink
        href={playerProfileHref(entry.userId)}
        className={cn(ROW_LINK_CLASS, revealClass)}
        style={style}
      >
        <UserAvatar
          userId={entry.userId}
          name={entry.username ?? undefined}
          look={entry.look}
          size={32}
        />
        <PlayerName entry={entry} />
        {entry.isMe ? <YouTag /> : null}
      </SidebarLink>
      <Figures entry={entry} className={revealClass} style={style} />
    </div>
  );
}

/** The signed-in player's own place under the top three (Figma 15001:34597). */
export function OwnRow({ entry, reveal = false, order = 0 }: RowProps) {
  const format = useFormatter();
  const { className: revealClass, style } = revealStyle(reveal, order);
  return (
    <div
      className={cn('flex items-center gap-2 border-t border-white/10 pt-2 pb-[3px]', revealClass)}
      style={style}
    >
      <span className="shrink-0 text-center text-[10px] leading-[normal] text-v2-text-secondary tabular-nums">
        {format.number(entry.position)}
      </span>
      <SidebarLink href={playerProfileHref(entry.userId)} className={ROW_LINK_CLASS}>
        <UserAvatar
          userId={entry.userId}
          name={entry.username ?? undefined}
          look={entry.look}
          size={24}
        />
        <PlayerName entry={entry} />
        <YouTag />
      </SidebarLink>
      <Figures entry={entry} />
    </div>
  );
}

/** Volume over tasks as two bars, in the figures' own height. */
function FiguresSkeleton() {
  return (
    <span className="flex h-8 shrink-0 flex-col items-end justify-center gap-2">
      <Skeleton className="h-3 w-14 bg-v2-surface-tertiary" />
      <Skeleton className="h-2.5 w-10 bg-v2-surface-tertiary" />
    </span>
  );
}

/** Name-width bar; the rest of the row's middle stays empty, like a short nickname. */
function NameSkeleton() {
  return (
    <span className="min-w-0 flex-1">
      <Skeleton className="h-3.5 w-24 bg-v2-surface-tertiary" />
    </span>
  );
}

interface LeaderRowSkeletonProps {
  place: number;
}

/** Placeholder of a top-three row while the board loads; the place disc stays. */
export function LeaderRowSkeleton({ place }: LeaderRowSkeletonProps) {
  return (
    <div className="flex h-10 items-center gap-2">
      <PlaceCoin place={place} />
      <Skeleton className="size-8 shrink-0 rounded-full bg-v2-surface-tertiary" />
      <NameSkeleton />
      <FiguresSkeleton />
    </div>
  );
}
